import { useState } from 'react';
import { useLang } from '../../../context/LanguageContext';
import { submitForm } from '../../../services/formSubmit';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const { t, lang } = useLang();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('loading');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (typeof value === 'string') data.append(key, value);
    });
    try {
      const res = await submitForm(data, { subject: 'Nuevo mensaje de contacto — SpartechRD' });
      if (!res.ok) throw new Error();
      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch {
      setStatus('error');
    }
  }

  return (
    <section className="w-full bg-white py-24 px-6" id="contacto">
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <span className="inline-block text-red-500 text-xs font-extrabold uppercase tracking-widest mb-4">
              {t('contact_badge')}
            </span>
            <h2 className="text-white text-3xl md:text-4xl font-extrabold uppercase tracking-wider mb-6 leading-tight">
              {t('contact_heading1')}<br />
              <span className="text-red-500">{t('contact_heading2')}</span>
            </h2>
            <p className="text-zinc-400 text-sm leading-relaxed mb-10 max-w-sm">
              {t('contact_body')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-800 flex-shrink-0">
                  <i className="ri-phone-line text-red-500 text-lg" />
                </div>
                <div>
                  <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">
                    {t('contact_phone_label')}
                  </p>
                  <a href="tel:8295961615" className="text-white text-sm hover:text-red-400 transition-colors cursor-pointer block">
                    (829) 596-1615
                  </a>
                  <a href="tel:8492081166" className="text-white text-sm hover:text-red-400 transition-colors cursor-pointer block">
                    (849) 208-1166
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-800 flex-shrink-0">
                  <i className="ri-map-pin-line text-red-500 text-lg" />
                </div>
                <div>
                  <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">
                    {t('contact_location_label')}
                  </p>
                  <p className="text-white text-sm leading-relaxed">{t('contact_location_val')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-800 flex-shrink-0">
                  <i className="ri-time-line text-red-500 text-lg" />
                </div>
                <div>
                  <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">
                    {t('contact_hours_label')}
                  </p>
                  <p className="text-white text-sm">{t('contact_hours_val')}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{t('contact_hours_closed')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-800 flex-shrink-0">
                  <i className="ri-whatsapp-line text-red-500 text-lg" />
                </div>
                <div>
                  <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">
                    {t('contact_wa_label')}
                  </p>
                  <a
                    href="https://wa.me/18492081166"
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="text-white text-sm hover:text-emerald-400 transition-colors cursor-pointer"
                  >
                    {t('contact_wa_cta')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="bg-zinc-900 rounded-3xl p-8">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-500/15 mb-5">
                  <i className="ri-check-double-line text-emerald-500 text-3xl" />
                </div>
                <h3 className="text-white font-extrabold text-xl mb-2">{t('contact_success_title')}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">{t('contact_success_desc')}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-xs font-bold text-red-500 uppercase tracking-wider hover:underline cursor-pointer whitespace-nowrap"
                >
                  {t('contact_another')}
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-spartech-rd"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <h3 className="text-white font-extrabold text-base uppercase tracking-wider mb-6">
                  {t('contact_form_title')}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-2">
                      {t('contact_name')} *
                    </label>
                    <input
                      name="nombre"
                      type="text"
                      required
                      placeholder={t('contact_name')}
                      className="w-full bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-2">
                      {t('contact_email')} *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="tu@correo.com"
                      className="w-full bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-2">
                    {t('contact_phone')}
                  </label>
                  <input
                    name="telefono"
                    type="tel"
                    placeholder="(809) 000-0000"
                    className="w-full bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-2">
                    {t('contact_subject_label')}
                  </label>
                  <select
                    name="asunto"
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-500 transition-colors cursor-pointer"
                  >
                    <option value="consulta">{lang === 'en' ? 'General inquiry' : 'Consulta general'}</option>
                    <option value="ensamblaje">{lang === 'en' ? 'Custom build' : 'Ensamblaje personalizado'}</option>
                    <option value="empresas">{lang === 'en' ? 'Enterprise solutions' : 'Soluciones para empresas'}</option>
                    <option value="soporte">{lang === 'en' ? 'Tech support' : 'Soporte técnico'}</option>
                    <option value="cotizacion">{lang === 'en' ? 'Request quote' : 'Solicitar cotización'}</option>
                    <option value="otro">{lang === 'en' ? 'Other' : 'Otro'}</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest">
                      {t('contact_message')} *
                    </label>
                    <span className={`text-[10px] font-medium ${charCount > 480 ? 'text-red-400' : 'text-zinc-600'}`}>
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="mensaje"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder={t('contact_message')}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-500 transition-colors resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-400 text-xs font-medium">{t('contact_error')}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading' || charCount > 500}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-extrabold text-sm uppercase tracking-widest py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? t('contact_sending') : t('contact_send_btn')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
